import React, { useEffect, useCallback, useState } from 'react'
import { inject } from 'mobx-react'
import { RouteComponentProps } from 'react-router'
import { History } from 'history'
import { PageContent } from '../../components/Common/PageContent'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import usePagination from '../../utils/usePagination'
import { API, Post, RemoteResource, Timeline } from '../../services/api'
import { WORKSPACES_STORE } from '../../stores'
import { WorkspacesStore } from '../../stores/WorkspaceStore'
import { FetchingLoader } from '../../components/FetchingLoader'
import { PostCard } from './components/PostCard'
import { LoaderBlock } from '../../components/Spinner'
import { PhotoDisplayer } from '../../components/PhotoDisplayer'
import { useEmojis } from './hooks/useEmojis'

const POSTS_PER_PAGE = 30

interface TimelineDetailParams {
  workspaceSlug: string
  timelineId: string
}

interface TimelineDetailScreenProps extends RouteComponentProps<TimelineDetailParams> {
  [WORKSPACES_STORE]: WorkspacesStore
  history: History
}

/** Group posts by date (dd MMMM yyyy in Polish locale). */
const groupPostsByDate = (posts: Post[]): Map<string, Post[]> => {
  const groups = new Map<string, Post[]>()
  for (const post of posts) {
    const d = new Date(post.date || post.created_at)
    const key = d.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(post)
  }
  return groups
}

const formatTime = (dateStr: string): string => {
  const d = new Date(dateStr)
  return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
}

const TimelineDetailScreen: React.FC<TimelineDetailScreenProps> = (props) => {
  const workspacesStore = props[WORKSPACES_STORE]
  const workspaceId = workspacesStore.activeWorkspaceId
  const workspaceSlug = workspacesStore.activeWorkspaceSlug
  const { timelineId } = props.match.params

  const [timeline, setTimeline] = React.useState<Timeline | null>(null)
  const [loadingTimeline, setLoadingTimeline] = React.useState(true)

  // Fetch custom emojis
  const { emojis } = useEmojis(workspaceId)

  // Photo displayer state
  const [previewPhotos, setPreviewPhotos] = useState<RemoteResource[]>([])
  const [previewPhotoId, setPreviewPhotoId] = useState<string | null>(null)

  const openPhotoDisplayer = useCallback((post: Post, resourceIndex: number) => {
    const resource = post.resources[resourceIndex]
    if (!resource) return
    setPreviewPhotos(post.resources)
    setPreviewPhotoId(resource.id)
  }, [])

  const closePhotoDisplayer = useCallback(() => {
    setPreviewPhotoId(null)
    setPreviewPhotos([])
  }, [])

  // Fetch timeline metadata
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoadingTimeline(true)
      // We don't have a dedicated getTimeline(id) endpoint,
      // so we fetch the list filtered by the id. If the API returns
      // only one matching timeline we use that; otherwise fall back.
      const resp = await API.getInstance().getTimelines({
        workspaceId,
        count: 100,
      })
      if (!cancelled && resp.ok && resp.data) {
        const found = resp.data.find((t) => t.id === timelineId)
        if (found) setTimeline(found)
      }
      if (!cancelled) setLoadingTimeline(false)
    })()
    return () => {
      cancelled = true
    }
  }, [workspaceId, timelineId])

  // Paginated posts
  const fetchPostsPage = useCallback(
    async (page: number, perPage: number) => {
      const resp = await API.getInstance().getTimelinePosts({
        workspaceId,
        timelineId,
        from: page * perPage,
        count: perPage,
        order: 'desc',
      })
      if (resp.ok && resp.data) return resp.data
      throw new Error(JSON.stringify(resp))
    },
    [workspaceId, timelineId],
  )

  const { data: posts, isFetching, allFetched, fetchNext, reset } = usePagination<Post>({
    fetchPage: fetchPostsPage,
    perPage: POSTS_PER_PAGE,
  })

  useEffect(() => {
    reset()
    setTimeout(() => fetchNext().catch(() => {}))
  }, [workspaceId, timelineId])

  // Scroll-based fetch
  useEffect(() => {
    if (allFetched || isFetching || posts.length === 0) return

    const handleScroll = () => {
      const scrollBottom = window.innerHeight + window.scrollY
      const threshold = document.documentElement.scrollHeight - 300
      if (scrollBottom >= threshold && !allFetched && !isFetching) {
        fetchNext().catch(() => {})
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [posts.length, allFetched, isFetching, fetchNext])

  const dateGroups = groupPostsByDate(posts)

  if (loadingTimeline) {
    return (
      <PageContent maxWidth="max-w-2xl">
        <LoaderBlock />
      </PageContent>
    )
  }

  return (
    <PageContent maxWidth="max-w-2xl">
      {/* Cover photo */}
      {timeline?.cover_photo && (
        <div className="mb-6 -mx-4 md:-mx-0 overflow-hidden md:rounded-xl">
          <img
            src={timeline.cover_photo.urls.large}
            alt={timeline.name}
            className="w-full h-48 md:h-64 object-cover"
          />
        </div>
      )}

      {/* Header */}
      <div className="mb-10 flex items-start gap-3">
        <button
          onClick={() => props.history.push(`/${workspaceSlug}/timelines`)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full hover:bg-gray-100 transition-colors mt-0.5"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-gray-900 truncate">
            {timeline?.name || 'Oś czasu'}
          </h1>
          {timeline?.description && (
            <p className="mt-1.5 text-sm text-gray-500">{timeline.description}</p>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="relative pb-12">
        {/* Vertical line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 -translate-x-1/2" />

        {Array.from(dateGroups.entries()).map(([dateLabel, datePosts]) => (
          <div key={dateLabel}>
            {/* Date pill */}
            <div className="relative z-10 flex justify-center mb-6 mt-2">
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-700">
                {dateLabel}
              </div>
            </div>

            {datePosts.map((post) => (
              <div key={post.id} className="relative mb-8">
                {/* Time marker */}
                <div className="relative z-10 flex justify-center">
                  <div className="h-12 w-12 rounded-full border border-gray-300 bg-white flex items-center justify-center text-[11px] font-semibold text-gray-700">
                    {formatTime(post.date || post.created_at)}
                  </div>
                </div>

                {/* Post card */}
                <div className="relative mx-4 md:mx-8">
                  {/* Triangle pointing up from card to circle */}
                  <div className="flex justify-center -mb-px">
                    <div
                      className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[14px]"
                      style={{ borderBottomColor: post.color || '#fde68a' }}
                    />
                  </div>
                  <PostCard
                    post={post}
                    emojis={emojis}
                    onImageClick={(idx) => openPhotoDisplayer(post, idx)}
                  />
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Empty state */}
        {!isFetching && posts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <p className="text-lg">Brak postów</p>
            <p className="text-sm">Ta oś czasu nie zawiera jeszcze żadnych postów.</p>
          </div>
        )}

        {/* Beginning marker */}
        {!isFetching && allFetched && posts.length > 0 && (
          <div className="relative z-10 flex justify-center mt-4">
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-center">
              <p className="text-sm font-medium text-gray-500">Początek osi czasu</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {new Date(timeline?.created_at || '').toLocaleDateString('pl-PL', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>
          </div>
        )}
      </div>

      {isFetching && <FetchingLoader />}

      {/* Photo displayer overlay */}
      {previewPhotoId && previewPhotos.length > 0 && (
        <PhotoDisplayer
          photos={previewPhotos}
          photoId={previewPhotoId}
          onClose={closePhotoDisplayer}
          onDelete={closePhotoDisplayer}
          onNavigate={(id) => setPreviewPhotoId(id)}
        />
      )}
    </PageContent>
  )
}

export default inject(WORKSPACES_STORE)(TimelineDetailScreen)
